import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { RANGE_PRESETS, lastNDays } from '@/lib/dateRange';
import type { DateRange, Me } from '@/lib/types';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

function approximateDays(range: DateRange): number {
  return Math.round(
    (Date.parse(range.to) - Date.parse(range.from)) / 86_400_000,
  );
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const days = approximateDays(value);
  const preset = RANGE_PRESETS.find((p) => p.days === days);
  const label = preset?.label ?? `Last ${days} days`;
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => (await api.get<Me>('/v1/me')).data,
    staleTime: 5 * 60_000,
  });
  const tz = me.data?.timezone ?? 'UTC';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Calendar className="h-4 w-4" />
          {label}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {RANGE_PRESETS.map((p) => (
          <DropdownMenuItem
            key={p.days}
            onSelect={() => onChange(lastNDays(p.days))}
          >
            {p.label}
          </DropdownMenuItem>
        ))}
        <div className="border-t px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          Days in {tz}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
